import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth, database, getSecureAtomicTime } from '../../services/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as updateFirebasePassword
} from 'firebase/auth';
import { ref, set, get, update, runTransaction } from 'firebase/database';
import toast from 'react-hot-toast';
import { normalizeUserRecord } from '../../utils/helpers';

const fetchUserProfile = async (uid) => {
  if (!uid) {
    throw new Error('User ID is required');
  }

  const paths = [`Users/${uid}`, `users/${uid}`];
  for (const path of paths) {
    try {
      const snapshot = await get(ref(database, path));
      if (snapshot.exists()) {
        return snapshot.val();
      }
    } catch (error) {
      // Failed to fetch from this path, try next
    }
  }
  return null;
};

// Friendly auth error mapper (focus on login errors)
const getAuthErrorMessage = (error) => {
  const code = error?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/invalid-password':
      return 'Incorrect email or password.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again later or reset your password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Unable to sign in. Please try again.';
  }
};

const getPasswordChangeErrorMessage = (error) => {
  const code = error?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Your current password is incorrect.';
    case 'auth/weak-password':
      return 'Your new password must be at least 6 characters long.';
    case 'auth/too-many-requests':
      return 'Too many attempts were made. Please wait a bit and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/requires-recent-login':
      return 'Please sign in again and then try changing your password.';
    default:
      return 'Unable to change your password right now.';
  }
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user?.uid) {
        throw new Error('Authentication failed');
      }

      const rawProfile = await fetchUserProfile(user.uid);
      const userData = normalizeUserRecord(rawProfile, {
        email: user.email,
        name: user.displayName || '',
        orders: {},
        totalSpent: 0,
        rewardPoints: 0,
        telegramUsername: '',
      });

      // BLOCK LOGIN FOR DISABLED ACCOUNTS
      if (userData.isDisabled) {
        await signOut(auth); // Revoke Firebase session
        const banMsg = 'This account has been disabled. Please contact support.';
        toast.error(banMsg);
        return rejectWithValue(banMsg);
      }

      toast.success('Welcome back!');
      return {
        uid: user.uid,
        email: userData.email || user.email,
        displayName: userData.name || user.displayName,
        photoURL: user.photoURL,
        ...userData,
      };
    } catch (error) {
      const message = getAuthErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password, name, referralCode, country, language, timezone }, { rejectWithValue }) => {
    try {
      if (!email || !password || !name || !country || !language) {
        throw new Error('Email, password, name, country, and language are required');
      }

      // Validate referral code format if provided
      const rawReferral = (referralCode || '').trim().toUpperCase();
      if (rawReferral) {
        const REF_CODE_REGEX = /^PSH-[A-Z]{1,8}-[A-Z0-9]{4}$/;
        if (!REF_CODE_REGEX.test(rawReferral)) {
          const msg = 'Invalid referral code format. Example: PSH-USERNAME-AB12';
          toast.error(msg);
          return rejectWithValue(msg);
        }

        // Cross-check if the referral code actually exists in the database
        // Read only `owner`, never the whole node: the code's `referrals` subtree holds every
        // referred user's name and email, and this check runs before the visitor is signed in.
        const safeReferralInput = rawReferral.replace(new RegExp('[.#$/\\[\\]]', 'g'), '_');
        const refSnapshot = await get(ref(database, `referrals-codes/${safeReferralInput}/owner`));
        if (!refSnapshot.exists()) {
          const msg = 'The provided referral code does not exist. Please clear it or enter a valid code.';
          toast.error(msg);
          return rejectWithValue(msg);
        }
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user?.uid) {
        throw new Error('User creation failed');
      }

      await updateProfile(user, { displayName: name });

      const formattedDate = getSecureAtomicTime();
      const normalizedReferralInput = (rawReferral || '').trim(); // use validated code if any
      const safeReferralInput = normalizedReferralInput.replace(new RegExp('[.#$/\\[\\]]', 'g'), '_');

      // Sanitized generated code
      const userReferralCode = `PSH-${name.toUpperCase().slice(0, 8).replace(new RegExp('[\\s.#$\\[\\]/]', 'g'), '')}-${user.uid.slice(-4).toUpperCase()}`;

      const adminProfile = {
        'Personal details': {
          Name: name,
          Email: user.email,
          'Signup date': formattedDate,
          Country: country,
          Language: language,
          Timezone: timezone || '',
        },
        Orders: {},
        'Referral details': {
          'Referral Code': userReferralCode,
          'Referred By': normalizedReferralInput || ''
        },
        'Total Spent': 0,
        'Reward Points': 0,
        'Total order': 0,
        createdAt: formattedDate,
        Login_Count: 0,
        Max_login: 30
      };

      // Prepare database updates
      const updates = {};

      // 1. Create User Profile
      updates[`Users/${user.uid}`] = adminProfile;

      // 2. Create Master Referral Record
      updates[`referrals-codes/${userReferralCode}`] = {
        owner: user.uid,
        ownerName: name,
        createdAt: formattedDate
      };

      // 3. Log Referral Usage (if applicable)
      if (normalizedReferralInput) {
        // Use sanitized key for lookup
        updates[`referrals-codes/${safeReferralInput}/referrals/${user.uid}`] = {
          name: name,
          email: user.email,
          uid: user.uid,
          joinedAt: formattedDate,
          reward: 'pending'
        };
      }

      await update(ref(database), updates);

      const normalized = normalizeUserRecord(adminProfile, {
        email: user.email,
        name,
        referralCode: userReferralCode,
        referredBy: normalizedReferralInput || null,
        orders: {},
        totalSpent: 0,
        rewardPoints: 0,
        telegramUsername: '',
        country,
        language,
        timezone: timezone || '',
      });

      toast.success('Account created successfully! You can now place orders instantly.');
      return {
        uid: user.uid,
        email: normalized.email || user.email,
        displayName: normalized.name || name,
        photoURL: user.photoURL,
        ...normalized,
      };
    } catch (error) {
      toast.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
      return null;
    } catch (error) {
      toast.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
      return true;
    } catch (error) {
      toast.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const email = String(user?.email || '').trim();

      if (!user || !email) {
        throw new Error('User authentication required');
      }

      if (!currentPassword || !newPassword) {
        throw new Error('Current and new passwords are required');
      }

      const credential = EmailAuthProvider.credential(email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updateFirebasePassword(user, newPassword);

      toast.success('Password changed successfully!');
      return true;
    } catch (error) {
      const message = getPasswordChangeErrorMessage(error);
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateUserRewardPoints = createAsyncThunk(
  'auth/updateUserRewardPoints',
  async ({ pointsToDeduct }, { getState, rejectWithValue }) => {
    try {
      const { user } = getState().auth;
      if (!user?.uid) {
        throw new Error('User authentication required');
      }

      if (typeof pointsToDeduct !== 'number' || pointsToDeduct < 0) {
        throw new Error('Invalid points amount');
      }

      const pointsRef = ref(database, `Users/${user.uid}/Reward Points`);
      const transactionResult = await runTransaction(pointsRef, (current) => {
        const availablePoints = Number(current) || 0;
        if (availablePoints < pointsToDeduct) return;
        return availablePoints - pointsToDeduct;
      });

      if (!transactionResult.committed) {
        throw new Error('Insufficient reward points');
      }

      const newPoints = Number(transactionResult.snapshot.val()) || 0;

      return { newRewardPoints: newPoints, deductedPoints: pointsToDeduct };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update reward points');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ name, telegramUsername, country, language, timezone }, { getState, rejectWithValue }) => {
    try {
      const { user } = getState().auth;
      if (!user?.uid) {
        throw new Error('User authentication required');
      }

      const updates = {};
      if (name !== undefined && name.trim()) {
        updates['Personal details/Name'] = name.trim();
      }
      if (telegramUsername !== undefined) {
        updates['Personal details/Telegram Username'] = telegramUsername.trim();
      }
      if (country !== undefined) {
        updates['Personal details/Country'] = country;
      }
      if (language !== undefined) {
        updates['Personal details/Language'] = language;
      }
      if (timezone !== undefined) {
        updates['Personal details/Timezone'] = timezone;
      }

      if (Object.keys(updates).length === 0) {
        throw new Error('No valid updates provided');
      }

      const promises = Object.entries(updates).map(([path, value]) =>
        set(ref(database, `Users/${user.uid}/${path}`), value)
      );

      await Promise.all(promises);

      toast.success('Profile updated successfully!');
      return { name, telegramUsername, country, language, timezone };
    } catch (error) {
      toast.error('Failed to update profile');
      return rejectWithValue(error.message || 'Profile update failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    loading: false,
    error: null,
    isAuthenticated: false,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      // Update reward points
      .addCase(updateUserRewardPoints.fulfilled, (state, action) => {
        if (state.user) {
          state.user.rewardPoints = action.payload.newRewardPoints;
        }
      })
      // Update profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        if (state.user) {
          if (action.payload.name !== undefined) {
            state.user.name = action.payload.name;
            state.user.displayName = action.payload.name;
          }
      if (action.payload.telegramUsername !== undefined) {
        state.user.telegramUsername = action.payload.telegramUsername;
      }
      if (action.payload.telegramChatId !== undefined) {
        state.user.telegramChatId = action.payload.telegramChatId;
      }
      if (action.payload.telegramBoundAt !== undefined) {
        state.user.telegramBoundAt = action.payload.telegramBoundAt;
      }
      if (action.payload.country !== undefined) {
        state.user.country = action.payload.country;
      }
          if (action.payload.language !== undefined) {
            state.user.language = action.payload.language;
          }
          if (action.payload.timezone !== undefined) {
            state.user.timezone = action.payload.timezone;
          }
        }
      });
  },
});

export const { setUser, clearError } = authSlice.actions;
export default authSlice.reducer;
